import React, { useState } from 'react';
import { Tooltip } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle, faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';

import { SanityCounts } from '../api/workItems';
import { AreaOption, ViewKey, ViewOption, StateOption } from './WorkItemsView.constants';

type WorkItemsMetaProps = {
    sanityCounts: SanityCounts | null;
    currentView: ViewKey;
    stateFilter: string;
    areaFilter: string;
    /** True when `area` is present in the URL (including `all`); false for implicit active-domain default. */
    areaFilterExplicit?: boolean;
    areas: AreaOption[];
    views: ViewOption[];
    states: StateOption[];
    entityFilter: string | null;
    needsActionOnly: boolean;
    search: string;
    searchResultTotal?: number | null;
    hasActiveFilters: boolean;
    onClearAllFilters: () => void;
    onApplyOverduePreset: () => void;
};

const WorkItemsMeta: React.FC<WorkItemsMetaProps> = ({
    sanityCounts,
    currentView,
    stateFilter,
    areaFilter,
    areaFilterExplicit = false,
    areas,
    views,
    states,
    entityFilter,
    needsActionOnly,
    search,
    searchResultTotal = null,
    hasActiveFilters,
    onClearAllFilters,
    onApplyOverduePreset,
}) => {
    // Situational View collapsed by default — user can expand if they want the details
    const [detailsExpanded, setDetailsExpanded] = useState(false);

    const showSanityBar = Boolean(
        sanityCounts
        && (sanityCounts.overdue > 0 || sanityCounts.dueToday > 0 || sanityCounts.dueThisWeek > 0)
    );
    const showFacetSummary = Boolean(sanityCounts);
    const showOverduePreset = (sanityCounts?.overdue ?? 0) > 0;

    if (!showSanityBar && !showFacetSummary && !showOverduePreset && !hasActiveFilters) {
        return null;
    }

    return (
        <div className="space-y-2">
            {/* Compact summary row: timeline counts + overdue preset + details toggle */}
            {(showSanityBar || showOverduePreset || showFacetSummary) && (
                <div
                    className="flex flex-wrap items-center gap-4 px-4 py-2 rounded-xl text-xs font-medium"
                    style={{ backgroundColor: 'var(--color-bg-card)', border: '1px solid var(--color-border)' }}
                >
                    {/* Timeline urgency counts (always visible when present) */}
                    {showSanityBar && sanityCounts && (
                        <>
                            {sanityCounts.overdue > 0 && (
                                <Tooltip title="Due before today">
                                    <button
                                        onClick={onApplyOverduePreset}
                                        className="text-danger hover:underline transition-colors"
                                    >
                                        <span className="font-bold">{sanityCounts.overdue}</span> Overdue
                                    </button>
                                </Tooltip>
                            )}
                            {sanityCounts.dueToday > 0 && (
                                <Tooltip title="Due by end of day today">
                                    <span className="text-chip-warning">
                                        <span className="font-bold">{sanityCounts.dueToday}</span> Today
                                    </span>
                                </Tooltip>
                            )}
                            {sanityCounts.dueThisWeek > 0 && (
                                <Tooltip title="Due in the next 7 days (excludes today)">
                                    <span className="text-chip-indigo">
                                        <span className="font-bold">{sanityCounts.dueThisWeek}</span> Upcoming
                                    </span>
                                </Tooltip>
                            )}
                        </>
                    )}

                    {/* Details toggle (for Situational View) */}
                    {showFacetSummary && (
                        <button
                            onClick={() => setDetailsExpanded((prev) => !prev)}
                            className="ml-auto flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-theme-muted hover:text-theme-secondary transition-colors"
                        >
                            Details
                            <FontAwesomeIcon icon={detailsExpanded ? faChevronUp : faChevronDown} className="text-[8px]" />
                        </button>
                    )}
                </div>
            )}

            {/* Collapsible Situational View — hidden by default */}
            {detailsExpanded && showFacetSummary && sanityCounts && (
                <div className="flex flex-wrap items-center gap-4 px-4 py-2 text-[10px] font-bold uppercase tracking-widest text-theme-muted rounded-xl"
                    style={{ backgroundColor: 'var(--color-bg-card)', border: '1px solid var(--color-border)' }}
                >
                    <div className="flex items-center gap-1.5">
                        <FontAwesomeIcon icon={faInfoCircle} className="text-[8px] opacity-50" />
                        <span>Breakdown:</span>
                    </div>
                    <div className="flex items-center gap-3">
                        {currentView !== 'inbox' && (sanityCounts.byType?.['inbox'] ?? 0) > 0 && (
                            <span>Inbox: <span className="text-theme-secondary">{sanityCounts.byType?.['inbox'] ?? 0}</span></span>
                        )}
                        {currentView !== 'applications' && (sanityCounts.byType?.['application'] ?? 0) > 0 && (
                            <span>Apps: <span className="text-theme-secondary">{sanityCounts.byType?.['application'] ?? 0}</span></span>
                        )}
                        {currentView !== 'tasks' && (sanityCounts.byType?.['task'] ?? 0) > 0 && (
                            <span>Tasks: <span className="text-theme-secondary">{sanityCounts.byType?.['task'] ?? 0}</span></span>
                        )}
                        <div className="h-2 w-px mx-1" style={{ backgroundColor: 'var(--color-border)' }} />
                        <span>Email: <span className="text-chip-indigo">{sanityCounts.bySource?.['email'] ?? 0}</span></span>
                        <span>Upload: <span className="text-chip-info">{sanityCounts.bySource?.['document'] ?? 0}</span></span>
                        <span>Manual: <span className="text-theme-secondary">{sanityCounts.bySource?.['manual'] ?? 0}</span></span>
                    </div>
                    <Tooltip title="Counts reflect all items matching your current view, area, and state filters.">
                        <span className="ml-auto cursor-help opacity-40 hover:opacity-100 transition-opacity">
                            <FontAwesomeIcon icon={faInfoCircle} />
                        </span>
                    </Tooltip>
                </div>
            )}

            {/* Active filter breadcrumbs */}
            {hasActiveFilters && (
                <div className="flex items-center gap-2 px-4 py-2 text-xs bg-theme-secondary rounded-xl border border-theme">
                    <span className="text-theme-muted">Showing:</span>
                    {currentView !== 'all' && (
                        <span className="px-2 py-0.5 bg-theme-card border border-theme rounded-md text-theme">
                            {views.find((v) => v.id === currentView)?.label}
                        </span>
                    )}
                    {areaFilterExplicit && areaFilter !== 'all' && (
                        <span className="px-2 py-0.5 bg-theme-card border border-theme rounded-md text-theme">
                            {areas.find((a) => a.id === areaFilter)?.label}
                        </span>
                    )}
                    {stateFilter !== 'ACTIVE,TRIAGE' && (
                        <span className="px-2 py-0.5 bg-theme-card border border-theme rounded-md text-theme">
                            {states.find((s) => s.id === stateFilter)?.label}
                        </span>
                    )}
                    {entityFilter && (
                        <span className="px-2 py-0.5 bg-indigo-500/10 rounded-md text-chip-indigo">
                            Entity: {entityFilter}
                        </span>
                    )}
                    {needsActionOnly && (
                        <span className="px-2 py-0.5 bg-amber-500/10 rounded-md text-chip-warning">
                            Needs Action
                        </span>
                    )}
                    {search && (
                        <span className="px-2 py-0.5 bg-theme-card border border-theme rounded-md text-theme">
                            Search: &ldquo;{search}&rdquo;
                            {typeof searchResultTotal === 'number' && (
                                <span className="text-theme-muted">
                                    {' '}
                                    ({searchResultTotal} {searchResultTotal === 1 ? 'result' : 'results'})
                                </span>
                            )}
                        </span>
                    )}
                    <button
                        onClick={onClearAllFilters}
                        className="text-chip-indigo hover:underline font-bold ml-2 transition-colors"
                    >
                        Reset all
                    </button>
                </div>
            )}
        </div>
    );
};

export default WorkItemsMeta;
